import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Download, Gift, AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { buildAppSeo } from '../utils/seoContent';
import { fetchApp } from '../utils/adminApi';
import { useSite } from '../context/SiteContext';
import { resolveImage } from '../utils/imageHelper';
import { getBonusIcon, getWithdrawIcon } from '../utils/icons';
import { useToast } from '../hooks/use-toast';

const AppDetail = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const { apps, settings } = useSite() || { apps: [], settings: null };
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const BonusIcon = getBonusIcon(settings?.bonusIcon);
  const WithdrawIcon = getWithdrawIcon(settings?.withdrawIcon);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchApp(slug)
      .then(setApp)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleDownload = () => {
    if (app?.downloadLink) {
      window.open(app.downloadLink, '_blank', 'noopener,noreferrer');
      return;
    }
    toast({
      title: 'Download starting...',
      description: `${app?.name || 'App'} download will begin shortly.`,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="p-16 text-center text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin inline" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !app) {
    return (
      <div className="min-h-screen bg-white">
        <Seo
          title="App not found - GoGoYonoGames"
          description="The app you are looking for could not be found on GoGoYonoGames."
        />
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">App not found</h1>
          <p className="text-gray-600 mb-6">Sorry, we couldn’t find the app you’re looking for.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#6a1b7d] hover:bg-[#5a1668] text-white px-4 py-2 rounded-md transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // "Try out other apps" — 6 other apps from context (excluding current)
  const otherApps = apps.filter((a) => a.slug !== app.slug).slice(0, 6);

  const joiningBonus = (app.bonus || 41) + 10;
  const seo = buildAppSeo({ id: app.slug, name: app.name, bonus: app.bonus, minWithdraw: app.minWithdraw });
  // Prefer admin-provided custom content when available
  const finalTitle = app.customTitle || seo.title;
  const finalDescription = app.customMetaDescription || seo.description;
  const finalAbout = app.customAbout || `${seo.intro}\n\n${seo.benefit}\n\n${seo.whyChoose}`;
  const finalHindiAbout = app.customHindiAbout || `${seo.hindiIntro}\n\nBonus & Withdraw: ${seo.hindiBenefit}\n\nInstall कैसे करें: ${seo.hindiHow}`;
  const finalHowTo = app.customHowTo || seo.howTo;

  const jsonLdGraph = [
    {
      '@type': 'SoftwareApplication',
      name: app.name,
      operatingSystem: 'ANDROID',
      applicationCategory: 'GameApplication',
      image: app.image && (app.image.startsWith('http') ? app.image : (process.env.REACT_APP_BACKEND_URL || '') + app.image),
      description: finalDescription,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gogoyonogames.com/' },
        { '@type': 'ListItem', position: 2, name: app.name, item: `https://gogoyonogames.com/app/${app.slug}` },
      ],
    },
  ];
  if (app.customFaqs && app.customFaqs.length > 0) {
    jsonLdGraph.push({
      '@type': 'FAQPage',
      mainEntity: app.customFaqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }
  const jsonLd = { '@context': 'https://schema.org', '@graph': jsonLdGraph };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        image={app.image}
        type="article"
        jsonLd={jsonLd}
      />
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-600 mb-4">
          <Link to="/" className="hover:text-[#6a1b7d]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900 font-medium">{app.name}</span>
        </nav>

        {/* Hero */}
        <div className="flex flex-col items-center text-center">
          <img
            src={resolveImage(app.image)}
            alt={app.name}
            className="w-40 h-40 sm:w-56 sm:h-56 rounded-2xl object-cover shadow-lg"
          />
          <h1 className="mt-4 text-2xl sm:text-3xl font-bold text-gray-900">{app.name}</h1>
          <button
            onClick={handleDownload}
            className="mt-4 inline-flex items-center gap-2 bg-[#0f8a4a] hover:bg-[#0b6f3b] text-white text-base font-semibold px-6 py-3 rounded-md shadow transition-colors"
          >
            <Download className="w-5 h-5" /> Download App
          </button>
          {app.telegramLink && (
            <a
              href={app.telegramLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-2 text-sm text-[#2ea6e0] hover:underline"
            >
              Join our Telegram channel
            </a>
          )}
        </div>

        {/* Features */}
        <section className="mt-10">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
            App Features & Details
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-lg p-4">
              <BonusIcon className="w-6 h-6 text-[#c62828]" />
              <div>
                <div className="text-xs text-gray-600">Welcome Bonus</div>
                <div className="text-lg font-bold text-[#c62828]">₹{app.bonus}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-100 rounded-lg p-4">
              <Gift className="w-6 h-6 text-yellow-700" />
              <div>
                <div className="text-xs text-gray-600">Joining Bonus</div>
                <div className="text-lg font-bold text-yellow-700">₹{joiningBonus}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-lg p-4">
              <WithdrawIcon className="w-6 h-6 text-[#0f8a4a]" />
              <div>
                <div className="text-xs text-gray-600">Min Withdraw</div>
                <div className="text-lg font-bold text-[#0f8a4a]">₹{app.minWithdraw}</div>
              </div>
            </div>
          </div>

          <button
            onClick={handleDownload}
            className="mt-6 inline-flex items-center gap-2 bg-[#c62828] hover:bg-[#a52020] text-white text-base font-semibold px-6 py-3 rounded-md shadow transition-colors"
          >
            <Gift className="w-5 h-5" /> Claim Your Bonus Now!
          </button>
        </section>

        {/* About - admin content only, no auto-generated */}
        {app.customAbout && app.customAbout.trim() && (
          <section className="mt-10">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">About This App</h2>
            {app.customAbout.split(/\n\n+/).map((para, i) => (
              <p key={i} className="text-sm sm:text-base leading-relaxed text-gray-700 mb-3 whitespace-pre-line">{para}</p>
            ))}
          </section>
        )}

        {/* Hindi block - admin content only */}
        {app.customHindiAbout && app.customHindiAbout.trim() && (
          <section className="mt-10 bg-purple-50/50 border border-purple-100 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
              {app.name} — हिंदी में जानकारी
            </h2>
            {app.customHindiAbout.split(/\n\n+/).map((para, i) => (
              <p key={i} className="text-sm sm:text-base leading-relaxed text-gray-800 mb-3 whitespace-pre-line">{para}</p>
            ))}
          </section>
        )}

        {/* How to - admin content only */}
        {app.customHowTo && app.customHowTo.trim() && (
          <section className="mt-10">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
              How to Download {app.name}
            </h2>
            {app.customHowTo.split(/\n\n+/).map((para, i) => (
              <p key={i} className="text-sm sm:text-base leading-relaxed text-gray-700 mb-3 whitespace-pre-line">{para}</p>
            ))}
          </section>
        )}

        {/* Specs - only factual data */}
        <section className="mt-10">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
            {app.name} — App Details
          </h2>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm sm:text-base">
              <tbody>
                {[
                  ['App Name', app.name],
                  ['Platform', 'Android APK'],
                  ['Payment', 'UPI / Bank'],
                  ['Welcome Bonus', `₹${app.bonus}`],
                  ['Min Withdraw', `₹${app.minWithdraw}`],
                ].map(([k, v], i) => (
                  <tr key={k} className={`border-b border-gray-200 ${i % 2 === 0 ? 'bg-gray-50' : ''}`}>
                    <td className="px-4 py-2.5 font-semibold text-gray-700 w-1/3">{k}</td>
                    <td className="px-4 py-2.5 text-gray-900">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ - admin-added only */}
        {app.customFaqs && app.customFaqs.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
              {app.name} — Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {app.customFaqs.map((f, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1.5">Q{idx + 1}. {f.q}</p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Warning */}
        <div className="mt-8 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            <strong>Note:</strong> People below 18 years of age should not play these games.
            There may be risk of financial loss, so please play wisely and responsibly.
          </p>
        </div>

        {/* Try out other apps */}
        <section className="mt-12">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
            Try out other apps
          </h2>
          <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
            {otherApps.map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-3 px-3 sm:px-5 py-3 sm:py-4 hover:bg-gray-50 transition-colors">
                <Link to={`/app/${o.slug}`} className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <img src={resolveImage(o.image)} alt={o.name} className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover shadow flex-shrink-0" loading="lazy" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm sm:text-base font-semibold text-gray-900 truncate hover:text-[#6a1b7d] transition-colors">{o.name}</span>
                    <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-4">
                      <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-[#c62828] font-medium">
                        <BonusIcon className="w-3.5 h-3.5" />
                        Welcome Bonus ₹{o.bonus}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-[#0f8a4a] font-medium">
                        <WithdrawIcon className="w-3.5 h-3.5" />
                        Min Withdraw ₹{o.minWithdraw}
                      </span>
                    </div>
                  </div>
                </Link>
                <Link to={`/app/${o.slug}`} className="inline-flex items-center gap-1.5 bg-[#0f8a4a] hover:bg-[#0b6f3b] text-white text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-md shadow transition-colors flex-shrink-0">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Link>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AppDetail;
